import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// 组件导入
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Canvas from './components/Canvas';
import Gallery from './components/Gallery';
import Footer from './components/Footer';

// 类型定义
import { ImageModel, GeneratedImage, PromptPreset, PromptHistory, Tag, ImageFolder } from './types';

// 服务导入
import { fetchModels, generateImage } from './services/api';
import { saveAs } from 'file-saver';

const App: React.FC = () => {
  // 状态管理
  const [activeTab, setActiveTab] = useState<'create' | 'gallery'>('create');
  const [models, setModels] = useState<ImageModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<ImageModel | null>(null);
  const [prompt, setPrompt] = useState<string>('');
  const [negativePrompt, setNegativePrompt] = useState<string>(''); // 修改负面提示词的初始值
  const [aspectRatio, setAspectRatio] = useState<string>('1:1');
  // 默认使用超清画质
  const [quality, setQuality] = useState<string>('ultra');
  // 添加种子值状态
  const [seed, setSeed] = useState<string>('');
  // 添加生成数量状态，默认为1张
  const [count, setCount] = useState<number>(1);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [presets, setPresets] = useState<PromptPreset[]>([]);
  // 添加提示词历史记录状态
  const [promptHistory, setPromptHistory] = useState<PromptHistory[]>([]);
  
  // 添加标签相关状态
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // 添加收藏夹相关状态
  const [folders, setFolders] = useState<ImageFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [showFavorites, setShowFavorites] = useState<boolean>(false);

  // 初始化加载模型、提示词历史和标签/收藏夹数据
  useEffect(() => {
    const loadModels = async () => {
      try {
        const availableModels = await fetchModels();
        setModels(availableModels);
        if (availableModels.length > 0) {
          setSelectedModel(availableModels[0]);
        }
        
        // 加载随机提示词库 - 按不同模型类别分组
        const randomPrompts: PromptPreset[] = [
          // 写实风格提示词 - 适合核心基础模型
          { id: '1', name: '梦幻风景', prompt: '梦幻般的风景, 高山, 湖泊, 瀑布, 晚霞, 高清细节, 艺术感, 4K超高清', style: 'landscape', category: '核心基础模型' },
          { id: '2', name: '科幻城市', prompt: '未来科幻城市, 霓虹灯, 高楼大厦, 飞行器, 科技感, 细节丰富, 夜景, 光影效果', style: 'scifi', category: '核心基础模型' },
          { id: '3', name: '写实人像', prompt: '专业人像摄影, 自然光线, 浅景深, 精致五官, 完美肤质, 时尚服装, 工作室布景', style: 'portrait', category: '核心基础模型' },
          { id: '4', name: '自然风光', prompt: '壮丽自然风光, 日出, 山脉, 森林, 河流, 云海, 鸟瞰视角, 广角镜头, 高动态范围', style: 'nature', category: '核心基础模型' },
          { id: '5', name: '静物艺术', prompt: '精美静物摄影, 水果, 花卉, 玻璃器皿, 柔和光线, 质感表现, 专业布光, 高对比度', style: 'still-life', category: '核心基础模型' },
          { id: '21', name: '海洋世界', prompt: '深海场景, 珊瑚礁, 热带鱼群, 阳光穿透水面, 水下摄影, 蓝色渐变, 气泡效果, 高清质感', style: 'underwater', category: '核心基础模型' },
          { id: '22', name: '城市夜景', prompt: '城市夜景, 繁华都市, 车水马龙, 灯火通明, 摩天大楼, 反光效果, 长曝光, 都市生活', style: 'cityscape', category: '核心基础模型' },
          { id: '23', name: '雪山奇景', prompt: '壮丽雪山, 冰川, 极光, 星空, 雪松, 宁静湖泊, 冬季仙境, 震撼全景, 极致细节', style: 'winter', category: '核心基础模型' },
          { id: '24', name: '沙漠日落', prompt: '金色沙漠, 壮丽日落, 沙丘剪影, 仙人掌, 暖色调, 广袤无垠, 自然纹理, 宁静氛围', style: 'desert', category: '核心基础模型' },
          { id: '25', name: '建筑艺术', prompt: '宏伟建筑, 精细结构, 几何美学, 光影交错, 现代设计, 玻璃幕墙, 独特视角, 建筑摄影', style: 'architecture', category: '核心基础模型' },
          
          // 动漫风格提示词 - 适合动漫与二次元专用
          { id: '6', name: '动漫少女', prompt: '精美动漫少女, 细腻线条, 大眼睛, 精致服装, 飘逸长发, 校园场景, 日系插画风格', style: 'anime-girl', category: '动漫与二次元专用' },
          { id: '7', name: '动漫风景', prompt: '吉卜力风格动漫场景, 乡村, 自然, 细节丰富, 温暖阳光, 和平氛围, 梦幻色彩', style: 'anime-landscape', category: '动漫与二次元专用' },
          { id: '8', name: '赛博朋克', prompt: '赛博朋克动漫场景, 未来都市, 霓虹灯, 雨夜, 机械义肢, 高科技, 暗黑风格', style: 'cyberpunk-anime', category: '动漫与二次元专用' },
          { id: '9', name: '暗系动漫', prompt: '暗黑系动漫风格, 神秘氛围, 超自然元素, 恐怖场景, 怪物, 暗影, 紧张气氛', style: 'dark-anime', category: '动漫与二次元专用' },
          { id: '10', name: '动漫战斗', prompt: '动漫战斗场景, 激烈动作, 能量效果, 武器, 爆炸, 速度线, 强烈对比', style: 'anime-action', category: '动漫与二次元专用' },
          { id: '26', name: '魔法少女', prompt: '魔法少女动漫, 华丽变身, 闪亮特效, 魔法武器, 可爱造型, 梦幻背景, 战斗姿势, 彩虹色调', style: 'magical-girl', category: '动漫与二次元专用' },
          { id: '27', name: '机甲战士', prompt: '精密机甲, 未来战士, 机械细节, 金属质感, 驾驶舱视角, 战斗姿态, 科技武器, 动态构图', style: 'mecha', category: '动漫与二次元专用' },
          { id: '28', name: '幻想世界', prompt: '奇幻动漫场景, 浮空岛, 巨大树木, 水晶建筑, 魔法生物, 梦幻天空, 异世界风光, 细节丰富', style: 'fantasy-anime', category: '动漫与二次元专用' },
          { id: '29', name: '校园日常', prompt: '动漫校园场景, 樱花树下, 教室内部, 屋顶午餐, 放学时分, 夕阳余晖, 青春氛围, 日常生活', style: 'school-anime', category: '动漫与二次元专用' },
          { id: '30', name: '和风古风', prompt: '日本传统风格动漫, 和服角色, 古代建筑, 樱花飞舞, 竹林小径, 纸灯笼, 浮世绘风格, 细腻线条', style: 'japanese-anime', category: '动漫与二次元专用' },
          
          // 卡通风格提示词 - 适合欧美卡通与3D风格
          { id: '11', name: '迪士尼风格', prompt: '迪士尼风格角色, 可爱表情, 明亮色彩, 童话场景, 圆润线条, 温馨氛围', style: 'disney', category: '欧美卡通与3D风格' },
          { id: '12', name: '皮克斯3D', prompt: '皮克斯风格3D角色, 精细纹理, 逼真光影, 夸张表情, 温馨故事场景, 高品质渲染', style: 'pixar', category: '欧美卡通与3D风格' },
          { id: '13', name: '卡通动物', prompt: '可爱卡通动物角色, 拟人化, 富有表情, 明亮色彩, 简洁背景, 有趣姿势', style: 'cartoon-animal', category: '欧美卡通与3D风格' },
          { id: '14', name: '超级英雄', prompt: '卡通风格超级英雄, 夸张动作, 鲜艳服装, 城市背景, 战斗场景, 特效元素', style: 'superhero', category: '欧美卡通与3D风格' },
          { id: '15', name: '梦幻场景', prompt: 'Midjourney风格梦幻场景, 超现实元素, 奇幻色彩, 不可思议构图, 精细细节, 艺术感', style: 'midjourney', category: '欧美卡通与3D风格' },
          { id: '31', name: '蒸汽朋克', prompt: '蒸汽朋克卡通场景, 齿轮机械, 铜质装置, 维多利亚风格, 飞艇, 复古科技, 烟雾效果, 棕色调', style: 'steampunk', category: '欧美卡通与3D风格' },
          { id: '32', name: '太空冒险', prompt: '卡通太空冒险, 宇宙飞船, 外星球景观, 奇特生物, 星际探险, 科幻元素, 明亮星云, 未来科技', style: 'space-adventure', category: '欧美卡通与3D风格' },
          { id: '33', name: '童话世界', prompt: '童话风格插画, 魔法森林, 蘑菇屋, 小精灵, 奇幻生物, 彩虹桥, 梦幻色彩, 故事场景', style: 'fairytale', category: '欧美卡通与3D风格' },
          { id: '34', name: '游戏风格', prompt: '3D游戏风格场景, 奇幻角色, 魔法效果, 游戏UI元素, 战斗姿态, 宝箱道具, 任务场景, 鲜明色彩', style: 'game-style', category: '欧美卡通与3D风格' },
          { id: '35', name: '低多边形', prompt: '低多边形3D风格, 几何形状, 简化纹理, 鲜艳色块, 抽象场景, 现代感, 数字艺术, 简约设计', style: 'low-poly', category: '欧美卡通与3D风格' },
          
          // 艺术风格提示词 - 适合艺术与特殊效果
          { id: '16', name: '水彩艺术', prompt: '精美水彩画, 流动色彩, 透明效果, 纸张质感, 柔和边缘, 艺术构图, 情感表达', style: 'watercolor', category: '艺术与特殊效果' },
          { id: '17', name: '油画风格', prompt: '古典油画风格, 厚重笔触, 丰富色彩, 光影对比, 历史场景, 人物肖像, 细节丰富', style: 'oil-painting', category: '艺术与特殊效果' },
          { id: '18', name: '暗黑幻想', prompt: '暗黑幻想艺术, 神秘生物, 魔法元素, 哥特风格, 阴暗氛围, 超自然景观, 戏剧性光影', style: 'dark-fantasy', category: '艺术与特殊效果' },
          { id: '19', name: '抽象艺术', prompt: '现代抽象艺术, 几何形状, 大胆色彩, 非具象表达, 情感冲击, 艺术构成, 创意表现', style: 'abstract', category: '艺术与特殊效果' },
          { id: '20', name: '复古海报', prompt: '复古风格艺术海报, 20世纪设计, 鲜明色块, 简化形象, 排版元素, 做旧效果, 广告风格', style: 'vintage-poster', category: '艺术与特殊效果' },
          { id: '36', name: '印象派', prompt: '印象派艺术风格, 光影变化, 点彩技法, 户外场景, 自然光线, 柔和色调, 笔触可见, 氛围感强', style: 'impressionism', category: '艺术与特殊效果' },
          { id: '37', name: '波普艺术', prompt: '波普艺术风格, 流行文化元素, 大胆色彩, 重复图案, 漫画风格, 商业广告感, 现代都市, 鲜明对比', style: 'pop-art', category: '艺术与特殊效果' },
          { id: '38', name: '赛博朋克', prompt: '赛博朋克艺术, 霓虹灯效果, 未来都市, 数字雨, 全息投影, 机械改造人, 高科技与低生活, 反乌托邦', style: 'cyberpunk-art', category: '艺术与特殊效果' },
          { id: '39', name: '超现实主义', prompt: '超现实主义艺术, 梦境场景, 不可能构造, 悬浮物体, 错位空间, 象征意义, 心理暗示, 奇异组合', style: 'surrealism', category: '艺术与特殊效果' },
          { id: '40', name: '像素艺术', prompt: '复古像素艺术, 8位游戏风格, 方块构成, 有限调色板, 清晰边缘, 复古游戏场景, 简化形象, 怀旧感', style: 'pixel-art', category: '艺术与特殊效果' },
          
          // 新增类别：人物与角色 - 适合各种人物创作
          { id: '41', name: '科幻战士', prompt: '未来科幻战士, 高科技盔甲, 能量武器, 全息显示器, 未来战场, 机械义肢, 先进材质, 动态姿势', style: 'sci-fi-warrior', category: '人物与角色' },
          { id: '42', name: '奇幻角色', prompt: '奇幻角色肖像, 精灵特征, 魔法装备, 神秘氛围, 异世界背景, 华丽服饰, 魔法光效, 细节丰富', style: 'fantasy-character', category: '人物与角色' },
          { id: '43', name: '赛博格女孩', prompt: '赛博朋克女孩, 未来时尚, 霓虹灯反光, 机械植入物, 全息装饰, 都市背景, 科技配件, 冷色调', style: 'cyborg-girl', category: '人物与角色' },
          { id: '44', name: '古代武士', prompt: '日本武士肖像, 传统盔甲, 刀剑装备, 历史场景, 战斗姿态, 东方美学, 水墨风格, 强烈光影', style: 'samurai', category: '人物与角色' },
          { id: '45', name: '维京战士', prompt: '北欧维京战士, 皮革盔甲, 战斧武器, 龙船背景, 雪山环境, 部落纹身, 野性外观, 史诗感', style: 'viking', category: '人物与角色' },
          
          // 新增类别：风景与场景 - 适合环境创作
          { id: '46', name: '失落神庙', prompt: '古代失落神庙, 藤蔓缠绕, 破碎石柱, 神秘符文, 阳光透射, 尘埃粒子, 探险场景, 宏伟规模', style: 'lost-temple', category: '风景与场景' },
          { id: '47', name: '浮空城市', prompt: '天空中的浮空城市, 悬浮岛屿, 瀑布流下, 云海环绕, 飞行器, 未来建筑, 连接桥梁, 壮观景色', style: 'floating-city', category: '风景与场景' },
          { id: '48', name: '水晶洞穴', prompt: '神秘水晶洞穴, 发光晶体, 地下湖泊, 钟乳石, 反光效果, 蓝色调, 梦幻氛围, 探索场景', style: 'crystal-cave', category: '风景与场景' },
          { id: '49', name: '外星地貌', prompt: '外星球地貌, 奇异植被, 多重月亮, 异色天空, 不明生物, 奇怪岩石, 外星建筑, 科幻探索', style: 'alien-landscape', category: '风景与场景' },
          { id: '50', name: '末日废墟', prompt: '末日后废墟城市, 倒塌建筑, 生锈车辆, 荒凉街道, 野生植物入侵, 阴暗天空, 末日气氛, 生存场景', style: 'post-apocalyptic', category: '风景与场景' },
        ];
        setPresets(randomPrompts);
      } catch (error) {
        console.error('加载模型失败:', error);
        console.error('无法加载AI模型，请检查网络连接');
      }
    };

    loadModels();
    
    // 加载提示词历史记录
    const savedHistory = localStorage.getItem('promptHistory');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setPromptHistory(parsedHistory);
      } catch (error) {
        console.error('加载提示词历史记录失败:', error);
      }
    }
    
    // 加载标签数据
    const savedTags = localStorage.getItem('imageTags');
    if (savedTags) {
      try {
        const parsedTags = JSON.parse(savedTags);
        setTags(parsedTags);
      } catch (error) {
        console.error('加载标签数据失败:', error);
      }
    }
    
    // 加载收藏夹数据
    const savedFolders = localStorage.getItem('imageFolders');
    if (savedFolders) {
      try {
        const parsedFolders = JSON.parse(savedFolders);
        setFolders(parsedFolders);
      } catch (error) {
        console.error('加载收藏夹数据失败:', error);
      }
    }
  }, []);

  // 保存标签数据到本地存储
  useEffect(() => {
    if (tags.length > 0) {
      localStorage.setItem('imageTags', JSON.stringify(tags));
    }
  }, [tags]);
  
  // 保存收藏夹数据到本地存储
  useEffect(() => {
    if (folders.length > 0) {
      localStorage.setItem('imageFolders', JSON.stringify(folders));
    }
  }, [folders]);
  
  // 保存图片数据到本地存储
  useEffect(() => {
    if (generatedImages.length > 0) {
      localStorage.setItem('generatedImages', JSON.stringify(generatedImages));
    }
  }, [generatedImages]);

  // 添加标签相关函数
  const createTag = (name: string, color: string = '#3B82F6') => {
    const newTag: Tag = {
      id: `tag-${Date.now()}`,
      name,
      color,
      count: 0
    };
    
    setTags(prevTags => [...prevTags, newTag]);
    return newTag.id;
  };
  
  const deleteTag = (tagId: string) => {
    // 从所有图片中移除该标签
    const updatedImages = generatedImages.map(img => {
      if (img.tags && img.tags.includes(tagId)) {
        return {
          ...img,
          tags: img.tags.filter(id => id !== tagId)
        };
      }
      return img;
    });
    
    setGeneratedImages(updatedImages);
    setTags(prevTags => prevTags.filter(tag => tag.id !== tagId));
  };
  
  const addTagToImage = (imageId: string, tagId: string) => {
    const updatedImages = generatedImages.map(img => {
      if (img.id === imageId) {
        const currentTags = img.tags || [];
        if (!currentTags.includes(tagId)) {
          // 更新标签计数
          setTags(prevTags => 
            prevTags.map(tag => 
              tag.id === tagId ? { ...tag, count: tag.count + 1 } : tag
            )
          );
          
          return {
            ...img,
            tags: [...currentTags, tagId]
          };
        }
      }
      return img;
    });
    
    setGeneratedImages(updatedImages);
  };
  
  const removeTagFromImage = (imageId: string, tagId: string) => {
    const updatedImages = generatedImages.map(img => {
      if (img.id === imageId && img.tags && img.tags.includes(tagId)) {
        // 更新标签计数
        setTags(prevTags => 
          prevTags.map(tag => 
            tag.id === tagId ? { ...tag, count: Math.max(0, tag.count - 1) } : tag
          )
        );
        
        return {
          ...img,
          tags: img.tags.filter(id => id !== tagId)
        };
      }
      return img;
    });
    
    setGeneratedImages(updatedImages);
  };
  
  // 收藏夹相关函数
  const createFolder = (name: string, description: string = '') => {
    const newFolder: ImageFolder = {
      id: `folder-${Date.now()}`,
      name,
      description,
      imageIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setFolders(prevFolders => [...prevFolders, newFolder]);
    return newFolder.id;
  };
  
  const deleteFolder = (folderId: string) => {
    // 从文件夹中移除图片关联，但不删除图片
    const updatedImages = generatedImages.map(img => {
      if (img.folder === folderId) {
        return {
          ...img,
          folder: undefined
        };
      }
      return img;
    });
    
    setGeneratedImages(updatedImages);
    setFolders(prevFolders => prevFolders.filter(folder => folder.id !== folderId));
    
    // 如果当前选中的就是被删除的文件夹，清除选择
    if (selectedFolder === folderId) {
      setSelectedFolder(null);
    }
  };
  
  const addImageToFolder = (imageId: string, folderId: string) => {
    // 更新图片的文件夹关联
    const updatedImages = generatedImages.map(img => 
      img.id === imageId ? { ...img, folder: folderId } : img
    );
    
    setGeneratedImages(updatedImages);
    
    // 更新文件夹的图片列表
    setFolders(prevFolders => 
      prevFolders.map(folder => {
        if (folder.id === folderId && !folder.imageIds.includes(imageId)) {
          return {
            ...folder,
            imageIds: [...folder.imageIds, imageId],
            updatedAt: new Date().toISOString()
          };
        }
        return folder;
      })
    );
  };
  
  const removeImageFromFolder = (imageId: string, folderId: string) => {
    // 更新图片的文件夹关联
    const updatedImages = generatedImages.map(img => 
      img.id === imageId && img.folder === folderId ? { ...img, folder: undefined } : img
    );
    
    setGeneratedImages(updatedImages);
    
    // 更新文件夹的图片列表
    setFolders(prevFolders => 
      prevFolders.map(folder => {
        if (folder.id === folderId) {
          return {
            ...folder,
            imageIds: folder.imageIds.filter(id => id !== imageId),
            updatedAt: new Date().toISOString()
          };
        }
        return folder;
      })
    );
  };
  
  // 收藏/取消收藏图片
  const toggleFavorite = (imageId: string) => {
    const updatedImages = generatedImages.map(img => 
      img.id === imageId ? { ...img, isFavorite: !img.isFavorite } : img
    );
    
    setGeneratedImages(updatedImages);
  };

  // 生成图片
  const [previewImages, setPreviewImages] = useState<string[] | null>(null);

  const handleGenerate = async () => {
    if (!selectedModel) {
      console.error('请选择一个AI模型');
      return;
    }

    if (!prompt.trim()) {
      console.error('请输入提示词');
      return;
    }

    setIsGenerating(true);
    setPreviewImages(null);
    try {
      // 生成指定数量的图片
      const generatedImageResults = await generateImage({
        model: selectedModel.id,
        prompt: prompt,
        negative_prompt: negativePrompt,
        aspect_ratio: aspectRatio,
        quality: quality,
        seed: seed ? parseInt(seed) : undefined,
        count: count
      });
      
      // 收集所有生成的图片URL
      const previewUrls: string[] = generatedImageResults.map(img => img.url);
      
      // 将新生成的图片添加到列表中
      setGeneratedImages(prev => [...generatedImageResults, ...prev]);
      
      // 设置预览图片
      setPreviewImages(previewUrls);
      
      // 输出URL到控制台以便调试
      console.log('生成的图片URLs:', previewUrls);

      // 添加提示词到历史记录
      addToPromptHistory(prompt, negativePrompt);
      
      // 不再自动切换到画廊
      // setActiveTab('gallery');
    } catch (error) {
      console.error('生成图片失败:', error);
      console.error('生成图片失败:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // 此函数保留但不再使用，因为预设提示词功能已移除
  const usePreset = (preset: PromptPreset) => {
    setPrompt(preset.prompt);
    // 不显示任何提示
  };

  // 随机提示词
  const generateRandomPrompt = () => {
    const randomIndex = Math.floor(Math.random() * presets.length);
    setPrompt(presets[randomIndex].prompt);
    // 不显示任何提示
  };

  // 删除单张图片
  const removeImage = (imageId: string) => {
    setGeneratedImages(prev => prev.filter(img => img.id !== imageId));
  };
  
  // 清除画廊
  const clearGallery = () => {
    if (window.confirm('确定要清空画廊吗？此操作不可撤销。')) {
      setGeneratedImages([]);
      // 不显示提示
    }
  };

  // 添加提示词到历史记录
  const addToPromptHistory = (promptText: string, negativePromptText: string) => {
    if (!promptText.trim() || !selectedModel) return;
    
    const now = new Date();
    const newHistoryItem: PromptHistory = {
      id: `history-${Date.now()}`,
      prompt: promptText,
      negative_prompt: negativePromptText || undefined,
      model: selectedModel.id,
      created_at: now.toISOString(),
      used_count: 1
    };
    
    // 检查是否已存在相同的提示词
    const existingIndex = promptHistory.findIndex(
      item => item.prompt === promptText && item.negative_prompt === negativePromptText
    );
    
    if (existingIndex >= 0) {
      // 更新已存在的提示词使用次数和时间
      const updatedHistory = [...promptHistory];
      updatedHistory[existingIndex] = {
        ...updatedHistory[existingIndex],
        used_count: updatedHistory[existingIndex].used_count + 1,
        created_at: now.toISOString()
      };
      setPromptHistory(updatedHistory);
    } else {
      // 添加新的提示词到历史记录，限制最多保存20条
      setPromptHistory(prev => [newHistoryItem, ...prev].slice(0, 20));
    }
  };
  
  // 使用历史提示词
  const useHistoryPrompt = (historyItem: PromptHistory) => {
    setPrompt(historyItem.prompt);
    if (historyItem.negative_prompt) {
      setNegativePrompt(historyItem.negative_prompt);
    }
    
    // 更新使用次数
    const updatedHistory = promptHistory.map(item => 
      item.id === historyItem.id 
        ? { ...item, used_count: item.used_count + 1, created_at: new Date().toISOString() }
        : item
    );
    
    // 按最近使用时间重新排序
    updatedHistory.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    
    setPromptHistory(updatedHistory);
  };

  // 添加数据备份和恢复功能
  const exportData = () => {
    try {
      // 准备导出的数据
      const dataToExport = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        generatedImages,
        tags,
        folders,
        promptHistory
      };
      
      // 转换为JSON字符串
      const jsonString = JSON.stringify(dataToExport, null, 2);
      
      // 创建Blob对象
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // 使用file-saver库下载文件
      saveAs(blob, `ai-painter-backup-${new Date().toISOString().split('T')[0]}.json`);
      
      toast.success('数据导出成功！');
    } catch (error) {
      console.error('导出数据失败:', error);
      toast.error('导出数据失败，请查看控制台获取详细信息。');
    }
  };
  
  const importData = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target?.result as string);
          
          // 验证数据格式
          if (!jsonData.version || !jsonData.timestamp) {
            throw new Error('无效的备份文件格式');
          }
          
          // 确认导入
          if (window.confirm(`确定要导入此备份文件吗？\n创建时间: ${new Date(jsonData.timestamp).toLocaleString()}\n这将覆盖当前的所有数据！`)) {
            // 导入数据
            if (jsonData.generatedImages) setGeneratedImages(jsonData.generatedImages);
            if (jsonData.tags) setTags(jsonData.tags);
            if (jsonData.folders) setFolders(jsonData.folders);
            if (jsonData.promptHistory) setPromptHistory(jsonData.promptHistory);
            
            toast.success('数据导入成功！');
          }
        } catch (error) {
          console.error('解析导入文件失败:', error);
          toast.error('导入失败，文件格式无效。');
        }
      };
      
      reader.onerror = () => {
        toast.error('读取文件失败。');
      };
      
      reader.readAsText(file);
      
      // 重置文件输入，以便可以重新选择同一文件
      event.target.value = '';
    } catch (error) {
      console.error('导入数据失败:', error);
      toast.error('导入数据失败，请查看控制台获取详细信息。');
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* 移除右上角提示内容 */}
      <Header activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="flex-grow flex flex-col md:flex-row overflow-hidden">
        {activeTab === 'create' ? (
          <>
            <Sidebar 
              models={models}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              prompt={prompt}
              setPrompt={setPrompt}
              negativePrompt={negativePrompt}
              setNegativePrompt={setNegativePrompt}
              aspectRatio={aspectRatio}
              setAspectRatio={setAspectRatio}
              quality={quality}
              setQuality={setQuality}
              seed={seed}
              setSeed={setSeed}
              count={count}
              setCount={setCount}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              presets={presets}
              usePreset={usePreset}
              generateRandomPrompt={generateRandomPrompt}
              promptHistory={promptHistory}
              useHistoryPrompt={useHistoryPrompt}
            />
            <Canvas 
              prompt={prompt}
              selectedModel={selectedModel}
              isGenerating={isGenerating}
              aspectRatio={aspectRatio}
              previewImages={previewImages}
              count={count}
            />
          </>
        ) : (
          <>
            <Gallery 
              images={generatedImages}
              clearGallery={clearGallery}
              removeImage={removeImage}
              tags={tags}
              createTag={createTag}
              deleteTag={deleteTag}
              addTagToImage={addTagToImage}
              removeTagFromImage={removeTagFromImage}
              folders={folders}
              createFolder={createFolder}
              deleteFolder={deleteFolder}
              addImageToFolder={addImageToFolder}
              removeImageFromFolder={removeImageFromFolder}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              showFavorites={showFavorites}
              setShowFavorites={setShowFavorites}
              toggleFavorite={toggleFavorite}
              exportData={exportData}
              importData={importData}
            />
            
            {/* 隐藏的文件输入用于导入数据 */}
            <input 
              type="file" 
              id="import-data-input" 
              accept=".json" 
              onChange={importData} 
              style={{ display: 'none' }} 
            />
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default App;
